import type { GetServerSidePropsContext } from "next/types";

import Bugsnag from "@bugsnag/js";

import Page from "components/DashboardPage";
import {
  dashboardInsightsUrl,
  getSortOptions,
} from "components/DashboardPage/DashboardPageInsightList";
import type { NextPageWithLayout } from "pages/_app";
import {
  authenticatedAccountUrl,
  sessionFromContextCookie,
} from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";
import { isSsr } from "utils/isSsr";

const DashboardPage: NextPageWithLayout = () => {
  return <Page />;
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const collection = context.query.collection;
    const page = context.query.page && parseInt(context.query.page as string);
    const sort = context.query.sort as string;

    const session = sessionFromContextCookie(context);
    const authenticated = !!session?.authenticated?.token;
    const accountId = !!session?.authenticated?.accountId;
    const accountUrl = authenticatedAccountUrl(
      session?.authenticated?.account_id
    );

    const isMyReadingList = collection === "my-reading-list";

    const sortOptions = getSortOptions(isMyReadingList);
    const sortOption = sortOptions.find((o) => o.id === sort) || sortOptions[0];

    const options: any = {
      pageSize: authenticated && !page ? "12" : "5",
    };
    if (collection === "authors-picks") {
      options.filter = { conviction: "1" };
    } else if (collection === "trending") {
      options.filter = { collection: "trending" };
    } else {
      options.filter = { collection, accountId };
    }

    const publicationsUrl = dashboardInsightsUrl(
      page || 1,
      "",
      sortOption.sortStr,
      options
    );

    const [account, publications] = await Promise.all([
      isSsr(context) && accountUrl
        ? baseFetcher(accountUrl, context).catch(() => ({}))
        : Promise.resolve(null),
      isSsr(context) ? await baseFetcher(publicationsUrl, context) : null,
    ]);

    if (account?.isCorporateOfficer) {
      return {
        redirect: {
          permanent: false,
          destination: `/entities/${account?.company?.slug}`,
        },
      };
    }

    return {
      props: {
        fallback: {
          [accountUrl]: account,
          [`$inf$${publicationsUrl}`]: [publications],
        },
      },
    };
  } catch (error: any) {
    console.error(
      "[slug].tsx ~ getServerSideProps dashboard Page ~ error",
      error
    );
    Bugsnag.notify(error);
    return {
      props: {
        fallback: {},
      },
    };
  }
}

export default DashboardPage;
